import { apiClient } from "../../lib/apiClient";
import type { AdminInvite } from "../invites/api";

export interface TeamMember {
  user_id: string;
  email: string;
  name: string;
  status: string;
  joined_at: string;
  created_by: string | null;
  // Per-member last login (RFC3339) or null if the user has never signed in.
  last_login_at: string | null;
  // The UI treats every project membership as "Member" — no per-project role.
  role?: string;
}

export interface TeamResponse {
  members: TeamMember[];
  super_admins_count: number;
}

export async function getTeam(projectId: string): Promise<TeamResponse> {
  const r = await apiClient.get<TeamResponse>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/team`,
  );
  return r.data;
}

export async function removeMember(
  projectId: string,
  userId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/admin/projects/${encodeURIComponent(projectId)}/team/${encodeURIComponent(userId)}`,
  );
}

export async function leaveProject(
  projectId: string,
  userId: string,
): Promise<void> {
  await apiClient.post(
    `/api/admin/projects/${encodeURIComponent(projectId)}/team/${encodeURIComponent(userId)}/leave`,
  );
}

export async function inviteMember(
  projectId: string,
  email: string,
): Promise<void> {
  await apiClient.post(
    `/api/admin/projects/${encodeURIComponent(projectId)}/invites`,
    { email, role: "project_user" },
  );
}

// Pending invites scoped to a project. Backend accepts `game_id` as a filter
// on the global invites list endpoint (see internal/admin/api/handler/invites.go).
export async function getProjectPendingInvites(
  projectId: string,
): Promise<AdminInvite[]> {
  const path = `/api/admin/invites?status=pending&game_id=${encodeURIComponent(projectId)}`;
  const r = await apiClient.get<{ invites: AdminInvite[] }>(path);
  return r.data.invites;
}

export async function revokeInvite(inviteId: string): Promise<void> {
  await apiClient.post(
    `/api/admin/invites/${encodeURIComponent(inviteId)}/revoke`,
  );
}

// Resend a pending invite — bumps expires_at + re-sends the email.
// POST /api/admin/invites/{id}/resend → 200 (empty body). 404 not_found,
// 409 invite_not_pending and 403 forbidden surface as toasts.
export async function resendInvite(inviteId: string): Promise<void> {
  await apiClient.post(
    `/api/admin/invites/${encodeURIComponent(inviteId)}/resend`,
  );
}
